import { splitArray } from '@core/logic';
import { useAppDispatch } from '@core/redux/hooks';
import { elementSelectors, elementTypeSelectors } from '@elements/redux';
import { pipeFittingSelectors, pipeFittingThunks } from '@elements/redux/pipe-fitting.slice';
import { BundleType, SplineElement } from '@elements/types';
import { viewer3dSelectors } from '@viewer3D/redux';
import { createRef, useCallback, useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { PipeColor, pipeColors } from './pipeColors';
import type { Connection, Pipe, PipeFitting } from './types';

export const shortenName = (name: string) => name.replace(/SNRVe|\(|\)/g, '').trim();

export const usePipeFittingsRendering = () => {
  const selectedPointId = useSelector(viewer3dSelectors.selectedPointId);
  const splineElementsInPoint = useSelector(
    elementSelectors.selectByPointId(selectedPointId),
    shallowEqual,
  );
  const allElementTypes = useSelector(elementTypeSelectors.selectAll);
  const pipeFittingSelector = useMemo(
    () => pipeFittingSelectors.selectByProps({ pointId: selectedPointId }),
    [selectedPointId],
  );
  const pipeFittingsInPoint = useSelector(pipeFittingSelector, shallowEqual);
  const [outgoingElements, incomingElements] = splitArray(
    splineElementsInPoint,
    ({ fromPoint }) => !!fromPoint?.isFinished && fromPoint?.id === selectedPointId,
  );

  const incomingPipes = useMemo(() => {
    const toSpeedPipeView = (speedPipeTypeId: string, i: number): Pipe => {
      const [name, color] = pipeColors[i];
      return {
        id: speedPipeTypeId,
        name,
        color,
        ref: createRef<HTMLElement | null>(),
      };
    };

    const toPipeView = (e: SplineElement): Pipe => {
      const matched = allElementTypes.find((t) => t.id === e.splineElementTypeId) as BundleType;
      const name = matched?.name ?? '';
      const color = matched?.color ?? PipeColor.gr;
      const children = matched?.children;

      return {
        id: e.id,
        name: shortenName(name),
        color,
        ref: createRef<HTMLElement | null>(),
        children: !children?.length ? undefined : children?.map(toSpeedPipeView),
      };
    };

    return [
      ...incomingElements.map(toPipeView),
      {
        id: null,
        name: 'Unknown',
        color: PipeColor.gr,
        ref: createRef<HTMLElement | null>(),
      },
    ];
  }, [incomingElements, allElementTypes]);

  const outgoingPipes = useMemo(() => {
    const toSpeedPipeView = (speedPipeTypeId: string, i: number): Pipe => {
      const [name, color] = pipeColors[i];
      return {
        id: speedPipeTypeId,
        name,
        color,
        ref: createRef<HTMLElement | null>(),
      };
    };

    const toPipeView = (e: SplineElement): Pipe => {
      const matched = allElementTypes.find((t) => t.id === e.splineElementTypeId) as BundleType;
      const name = matched?.name ?? '';
      const color = matched?.color ?? PipeColor.gr;
      const children = matched?.children;

      return {
        id: e.id,
        name: shortenName(name),
        color,
        ref: createRef<HTMLElement | null>(),
        children: !children?.length ? undefined : children?.map(toSpeedPipeView),
      };
    };

    return [
      ...outgoingElements.map(toPipeView),
      {
        id: null,
        name: 'Unknown',
        color: PipeColor.gr,
        ref: createRef<HTMLElement | null>(),
      },
    ];
  }, [outgoingElements, allElementTypes]);

  const connections = useMemo(() => {
    const toConnectionsView = (pf: PipeFitting): Connection => {
      const inElementIndex = incomingElements.findIndex((e) => e.id === pf.incomingSplineElementId);
      const outElementIndex = outgoingElements.findIndex(
        (e) => e.id === pf.outgoingSplineElementId,
      );
      const inPipe = incomingPipes.at(inElementIndex)!;
      const outPipe = outgoingPipes.at(outElementIndex)!;

      return {
        id: pf.id,
        pointId: pf.pointId,
        pointElementId: pf.pointElementId,
        inPipe: inPipe.children?.find((c) => c.id === pf.incomingColor) ?? inPipe,
        outPipe: outPipe.children?.find((c) => c.id === pf.outgoingColor) ?? outPipe,
        fromSplineElementId: pf.incomingSplineElementId,
        toSplineElementId: pf.outgoingSplineElementId,
      };
    };

    return pipeFittingsInPoint.map(toConnectionsView);
  }, [pipeFittingsInPoint, incomingElements, outgoingElements, incomingPipes, outgoingPipes]);

  return useMemo(
    () => ({
      incomingPipes,
      connections,
      outgoingPipes,
    }),
    [incomingPipes, connections, outgoingPipes],
  );
};

export const useCreateFitting = () => {
  const selectedProjectId = useSelector(viewer3dSelectors.selectedProjectId)!;
  const selectedPointId = useSelector(viewer3dSelectors.selectedPointId);

  const dispatch = useAppDispatch();

  return useCallback(
    ({
      outgoingSplineElementId,
      outgoingColor,
      incomingSplineElementId,
      incomingColor,
    }: Partial<PipeFitting>) => {
      dispatch(
        pipeFittingThunks.post({
          path: ['projects', selectedProjectId, 'splineElementConnections'],
          body: {
            projectId: selectedProjectId,
            pointId: selectedPointId,
            outgoingSplineElementId,
            outgoingColor,
            incomingSplineElementId,
            incomingColor,
          },
        }),
      );
    },
    [dispatch, selectedProjectId, selectedPointId],
  );
};

export const useDeleteFitting = () => {
  const selectedProjectId = useSelector(viewer3dSelectors.selectedProjectId)!;
  const dispatch = useAppDispatch();

  return useCallback(
    (id: Connection['id']) =>
      dispatch(
        pipeFittingThunks.delete({
          id,
          path: ['projects', selectedProjectId, 'splineElementConnections', id],
        }),
      ),
    [dispatch, selectedProjectId],
  );
};
